import React, { useEffect } from 'react';
import { ActionIcon, Button, Flex, Highlight, Paper, ScrollArea, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { t } from 'i18next';
import { IconArrowRight, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useElementSize } from '@mantine/hooks';
import { useGetphysiologicalData } from '../../../global/entities/PhysiologicalData/queries/useGetPhysiologicalDatas';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';

import { theme } from '../../../global/style/theme';
import Utils from '../../../global/helpers/utils';
import { useDeletePhysiologicalData } from '../../../global/entities/PhysiologicalData/mutations/useDeletePhysiologicalDataMutations';
import { modals } from '@mantine/modals';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable';
import { useNavigate } from 'react-router-dom';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { RouteStore } from '../../../global/Store/RouteStore.ts';
interface PhysiologicalData {
    id: number;
    dateMeasured: Date;
    physiologicalTypeName: string;
    value: string | number;
    unitName: string;
}

const TableRow: React.FC<{
    data: PhysiologicalData;
    index: number;
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
    search: string;
}> = ({ data, index, onEdit, onDelete, search }) => {
    const dateTimeText = `${Utils.formatDateToYYYYMMDD(new Date(data.dateMeasured))} ${Utils.formatDateToTime(new Date(data.dateMeasured))}`;
    const isSearch =
        containsStringIgnoreCase(
            [
                dateTimeText,
                `${data.physiologicalTypeName}`,
                `${data.value}`,
                `${data.unitName}`,
            ],
            search,
        ) || !search;

    return (
        <Table.Tr
            style={{
                background: index % 2 === 1 ? 'var(--Background-Gray-light, #E9ECEF)' : undefined,
            }}
            hidden={!isSearch}
        >
            <Table.Td>
                <Highlight
                    highlight={search}
                    highlightStyles={{
                        color: 'var(--mantine-color-gray-9)',
                        fontWeight: 700,
                        WebkitBackgroundClip: 'text',
                    }}
                    styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                    truncate
                >
                    {dateTimeText}
                </Highlight>
            </Table.Td>
            <Table.Td>
                <Highlight
                    highlight={search}
                    highlightStyles={{
                        color: 'var(--mantine-color-gray-9)',
                        fontWeight: 700,
                        WebkitBackgroundClip: 'text',
                    }}
                    styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                    truncate
                >
                    {data.physiologicalTypeName}
                </Highlight>
            </Table.Td>
            <Table.Td>
                <Highlight
                    highlight={search}
                    highlightStyles={{
                        color: 'var(--mantine-color-gray-9)',
                        fontWeight: 700,
                        WebkitBackgroundClip: 'text',
                    }}
                    styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                    truncate
                >
                    {data.value}
                </Highlight>
            </Table.Td>
            <Table.Td>
                <Highlight
                    highlight={search}
                    highlightStyles={{
                        color: 'var(--mantine-color-gray-9)',
                        fontWeight: 700,
                        WebkitBackgroundClip: 'text',
                    }}
                    styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                    truncate
                >
                    {data.unitName}
                </Highlight>
            </Table.Td>
            <Table.Td>
                <Flex gap="xs">
                    <ActionIcon variant="subtle" color="red" onClick={() => onDelete(data.id)}>
                        <IconTrash size={18} />
                    </ActionIcon>
                    <ActionIcon onClick={() => onEdit(data.id)} variant="transparent">
                        <IconArrowRight color={theme.colors?.blue?.toString()} />
                    </ActionIcon>
                </Flex>
            </Table.Td>
        </Table.Tr>
    );
};

export type IPhysiologicalDataTableProps = {
    patientStore?: PatientStore;
    routeStore?: RouteStore;
};

const PhysiologicalDataTable: React.FC<IPhysiologicalDataTableProps> = inject(
    'patientStore',
    'routeStore',
)(
    observer(({ patientStore, routeStore }) => {
        const { data: physiologicalData } = useGetphysiologicalData(patientStore?.patientId);
        const { ref, width } = useElementSize();
        const deletePhysiologicalData = useDeletePhysiologicalData();
        const { setSorting, sortBy, reverseSortDirection, setSortedData, sortedData, search, setSearch } = useTable();
        const navigate = useNavigate();

        useEffect(() => {
            setSortedData([]);
            if (physiologicalData?.length) 
                setSortedData(physiologicalData);
            
        }, [physiologicalData]);

        useEffect(() => {
            routeStore?.setFromLocation();
        }, []);

        const handlePhysiologicalEdit = (id?: number) => {
            navigate(`../physiologicalData/${id}`);
        };

        const handlePhysiologicalDelete = (id: number) => {
            modals.openConfirmModal({
                title: t('modal.deletePhysiologicalDataModal.title'),
                children: (
                    <Stack gap={'sm'}>
                        <Text>{t('modal.deletePhysiologicalDataModal.question')}</Text>
                        <Text fw={'bold'}> {t('modal.deletePhysiologicalDataModal.details')}</Text>
                    </Stack>
                ),
                labels: {
                    confirm: t('modal.deletePhysiologicalDataModal.yes'),
                    cancel: t('modal.deletePhysiologicalDataModal.no'),
                },
                confirmProps: { color: 'red' },
                onConfirm: () => deletePhysiologicalData.mutate(id.toString()),
                size: 'lg',
                radius: 'xs',
            });
        };

        return (
            <Paper h={'min-content'} w={'100%'} ref={ref}>
                <Stack h={'100%'}>
                    <Title order={4}>{location.pathname.includes('physiologicalData') === false ? t('physiologicalData.title') : ''}</Title>
                    <Flex align={'center'} gap={'sm'}>
                        <TextInput
                            hidden={width < 450}
                            placeholder={t('global.search')}
                            rightSection={width < 450 ? null : <IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            style={{ flex: 1 }}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                        <Button
                            variant="filled"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => handlePhysiologicalEdit(0)}
                            w={width <= 450 ? '100%' : undefined}
                        >
                            {t('physiologicalData.addPhysiologicalData')}
                        </Button>
                    </Flex>
                    <ScrollArea w="100%">
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Th
                                    sorted={sortBy === 'dateMeasured'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('dateMeasured', sortedData)}
                                >
                                    <Stack gap={0}>
                                        {t('global.dateMeasured')}
                                        <Text size="xs" c="dimmed">
                                            {t('placeHolder.dateFormat')}
                                        </Text>
                                    </Stack>
                                </Th>
                                <Th
                                    sorted={sortBy === 'physiologicalTypeName'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('physiologicalTypeName', sortedData)}
                                >
                                    {t('global.description')}
                                </Th>
                                <Th
                                    sorted={sortBy === 'value'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('value', sortedData)}
                                >
                                    {t('global.value')}
                                </Th>
                                <Th
                                    sorted={sortBy === 'unitName'}
                                    reversed={reverseSortDirection}
                                    onSort={() => setSorting('unitName', sortedData)}
                                >
                                    {t('global.unit')}
                                </Th>
                                <Table.Th />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {sortedData?.map((row, index) => (
                                <TableRow
                                    key={row.id}
                                    data={row}
                                    index={index}
                                    onEdit={handlePhysiologicalEdit}
                                    onDelete={handlePhysiologicalDelete}
                                    search={search}
                                />
                            ))}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>
                </Stack>
            </Paper>
        );
    }),
);

const WrappedPhysiologicalDataTable: React.FC = () => {
    return (
        <TableContextProvider>
            <PhysiologicalDataTable />
        </TableContextProvider>
    );
};

export default WrappedPhysiologicalDataTable;
